import { useState, useEffect } from 'react';

// Channel ID untuk @islamiccentretvBangka
// https://www.youtube.com/channel/UCrHRnKfxBvT0-cicir4JjiQ
const DEFAULT_CHANNEL_ID = 'UCrHRnKfxBvT0-cicir4JjiQ';

/**
 * Hook untuk mengambil video terbaru dari YouTube channel menggunakan RSS Feed
 * Tidak memerlukan API key!
 */
export const useYouTubeVideos = (maxResults = 5) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        
        // RSS Feed URL
        const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${DEFAULT_CHANNEL_ID}`;
        
        // Coba beberapa CORS proxy
        const corsProxies = [
          { url: `https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`, type: 'allorigins' },
          { url: `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(rssUrl)}`, type: 'direct' },
        ];

        let xmlText = null;

        for (const proxy of corsProxies) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            
            const response = await fetch(proxy.url, {
              signal: controller.signal,
              headers: {
                'Accept': 'application/json, text/plain, */*',
              }
            });
            
            clearTimeout(timeoutId);

            if (!response.ok) continue;

            const data = await response.json();
            
            if (proxy.type === 'allorigins') {
              xmlText = data.contents;
            } else {
              xmlText = typeof data === 'string' ? data : JSON.stringify(data);
            }
            
            if (xmlText && xmlText.includes('<?xml')) {
              break;
            }
          } catch (err) {
            // Continue to next proxy
            continue;
          }
        }

        if (!xmlText) {
          throw new Error('Gagal mengambil data dari YouTube');
        }

        // Parse XML
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'application/xml');
        
        // Check for parse errors
        const parseError = xml.querySelector('parsererror');
        if (parseError) {
          throw new Error('Gagal memproses data YouTube');
        }

        // Get entries dengan namespace handling
        const entries = xml.getElementsByTagName('entry');
        
        if (entries.length === 0) {
          throw new Error('Tidak ada video ditemukan di channel');
        }

        const parsedVideos = Array.from(entries).slice(0, maxResults).map((entry, index) => {
          // Helper function untuk get element dengan namespace
          const getElementText = (tagName) => {
            const elements = entry.getElementsByTagName(tagName);
            return elements.length > 0 ? elements[0].textContent : '';
          };

          // Get video ID - coba beberapa cara
          let videoId = '';
          
          // Cara 1: yt:videoId
          const videoIdElements = entry.getElementsByTagNameNS('http://www.youtube.com/xml/schemas/2015', 'videoId');
          if (videoIdElements.length > 0) {
            videoId = videoIdElements[0].textContent;
          }
          
          // Cara 2: Dari ID element
          if (!videoId) {
            const idElement = entry.getElementsByTagName('id')[0];
            if (idElement) {
              const idText = idElement.textContent;
              const match = idText.match(/video:([a-zA-Z0-9_-]+)/);
              if (match) videoId = match[1];
            }
          }

          // Get title
          const title = getElementText('title') || 'Video Tanpa Judul';
          
          // Get description - coba media:group > media:description
          let description = '';
          const mediaGroup = entry.getElementsByTagNameNS('http://search.yahoo.com/mrss/', 'group')[0];
          if (mediaGroup) {
            const mediaDesc = mediaGroup.getElementsByTagNameNS('http://search.yahoo.com/mrss/', 'description')[0];
            if (mediaDesc) description = mediaDesc.textContent;
          }
          if (!description) {
            description = getElementText('description') || getElementText('content') || '';
          }
          
          // Get thumbnail
          let thumbnail = '';
          if (mediaGroup) {
            const mediaThumbnail = mediaGroup.getElementsByTagNameNS('http://search.yahoo.com/mrss/', 'thumbnail')[0];
            if (mediaThumbnail) {
              thumbnail = mediaThumbnail.getAttribute('url');
            }
          }
          // Fallback ke YouTube thumbnail URL
          if (!thumbnail && videoId) {
            thumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
          }

          // Get published date
          const publishedAt = getElementText('published') || getElementText('updated');

          return {
            id: `video-${index}`,
            videoId,
            title: title.trim(),
            description: description.length > 200 ? description.substring(0, 200) + '...' : description,
            thumbnail: thumbnail || `https://via.placeholder.com/320x180?text=Video+${index + 1}`,
            publishedAt,
          };
        }).filter(v => v.videoId); // Hanya yang punya video ID

        if (parsedVideos.length === 0) {
          throw new Error('Tidak dapat membaca data video');
        }

        setVideos(parsedVideos);
        setError(null);
      } catch (err) {
        console.error('YouTube RSS Error:', err);
        setError(err.message);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [maxResults]);

  return { 
    videos, 
    loading, 
    error,
    featuredVideo: videos[0] || null,
    playlistVideos: videos.slice(1),
    isUsingFallback: !!error 
  };
};

/**
 * Format relative time (e.g., "2 hari yang lalu")
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Baru saja';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} menit yang lalu`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} jam yang lalu`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} hari yang lalu`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} minggu yang lalu`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} bulan yang lalu`;
    return `${Math.floor(diffInSeconds / 31536000)} tahun yang lalu`;
  } catch {
    return '';
  }
};

/**
 * Format date (e.g., "15 Jan 2024")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};
